
import { Part, PartFilter } from "@/data/parts";
import { Table, TableBody, TableRow } from "@/components/ui/table";
import { usePartsFilter } from "@/hooks/use-parts-filter";
import { useTableColumns } from "./table/useTableColumns";
import { useTableSort } from "./table/useTableSort";
import { useTableDrag } from "./table/useTableDrag";
import PartTableHeader from "./table/PartTableHeader";
import PartTableCell from "./table/PartTableCell";
import NoPartsFound from "./table/NoPartsFound";
import { useIsMobile } from "@/hooks/use-mobile";

interface PartTableProps {
  parts: Part[];
  activeFilters: PartFilter[];
}

const PartTable = ({ parts, activeFilters }: PartTableProps) => {
  // Get filtering functions
  const { setSelectedCategory, setSelectedSubcategory, toggleFilter } = usePartsFilter();
  const isMobile = useIsMobile();
  
  // Use custom hooks for table functionality
  const { columns, setColumns, saveColumnOrder, isSaving } = useTableColumns();
  const { sortState, handleSort, getSortedParts } = useTableSort();
  const { handleDragStart, handleDragOver, handleDrop } = useTableDrag(columns, setColumns);
  
  // Get sorted parts
  const sortedParts = getSortedParts(parts);
  
  if (parts.length === 0) {
    return <NoPartsFound />;
  }

  return (
    <div className="w-full overflow-auto">
      <Table>
        {/* Only show header on desktop */}
        {!isMobile && (
          <PartTableHeader 
            columns={columns}
            sortState={sortState}
            handleSort={handleSort}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            saveColumnOrder={saveColumnOrder}
            isSaving={isSaving}
          />
        )}
        <TableBody>
          {sortedParts.map((part) => (
            <TableRow key={part.id}>
              {columns.map((column) => (
                <PartTableCell
                  key={`${part.id}-${column.id}`}
                  part={part}
                  columnId={column.id}
                  sortState={sortState}
                  activeFilters={activeFilters}
                  onCategoryClick={setSelectedCategory}
                  onSubcategoryClick={setSelectedSubcategory}
                  onFilterClick={toggleFilter}
                  onRatingSort={handleSort}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PartTable;
